/**
 * ROMATOKI — Packs (2026-09-11). Script for sections/romatoki-packs.liquid.
 * Replaces assets/romatoki-bundle.js.
 *
 * What it does, in order of the shopper's path:
 *   1. Pack row tapped  -> aria-checked on the row, the picker opens in
 *      place (data-open), quantities reset, the picker head scrolls into
 *      view if it is below the fold. Choosing another pack while the picker
 *      is open swaps its contents in place — no collapse-and-reopen.
 *   2. Gender segmented control -> data-active on the control (CSS slides
 *      the thumb), the other colour list is hidden, the range line updates.
 *      Quantities in the hidden list are KEPT: a pack may mix genders.
 *   3. Steppers, capped: sum(quantities) never exceeds the pack size; "+"
 *      is disabled at the cap, "-" at zero. The count line and the CTA
 *      reflect the sum.
 *   4. Add to bag, enabled only when sum == pack size: ADDITIVE write via
 *      /cart/add.js (a second pack stacks on purpose; the checkout total is
 *      the truth), Horizon told through the shopify:cart:lines-update event
 *      with didError:false so its cart component re-renders, then the drawer
 *      opens (or /cart, if no drawer). The picker resets after a success.
 *
 * Prices never live here. Totals are copied from the chosen row's rendered
 * text, which Liquid formatted with `money` from the section's settings, so
 * there is exactly one price table (see the section's header comment).
 *
 * Motion is CSS-only and follows Apple's HIG "Motion" page: this script
 * never waits on a transition and never disables anything while one runs,
 * so every animation is interruptible. Reduce Motion only affects the one
 * scroll this script performs.
 */
(function () {
  'use strict';

  var CART_LINES_UPDATE = 'shopify:cart:lines-update';
  var SOURCE = 'romatoki-packs';

  function reduceMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function shopRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    return root.replace(/\/$/, '');
  }

  /** Section ids of every live cart-items-component, so /cart/add.js can
   *  return the re-rendered drawer HTML in the same round trip. */
  function cartSectionIds() {
    var ids = [];
    var nodes = document.querySelectorAll('cart-items-component');
    for (var i = 0; i < nodes.length; i++) {
      var id = nodes[i].getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    }
    return ids;
  }

  function makeDeferred() {
    var d = {};
    d.promise = new Promise(function (resolve, reject) {
      d.resolve = resolve;
      d.reject = reject;
    });
    return d;
  }

  function openCartDrawer() {
    var drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && drawer.hasAttribute('open')) return true;
    if (window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.openCart === 'function') {
      window.Shopify.actions.openCart();
      return true;
    }
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    return false;
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function initPacks(root) {
    if (!root || root.__rtPacks) return;
    root.__rtPacks = true;

    var gallery = root.querySelector('[data-product-gallery]');
    if (gallery) {
      var mainImage = gallery.querySelector('.rt-pivot__main-image');
      var thumbnails = toArray(gallery.querySelectorAll('[data-gallery-thumb]'));
      var galleryStatus = gallery.querySelector('[data-gallery-status]');
      var imageRequest = 0;
      var strip = gallery.querySelector('.rt-pivot__detail-strip');
      var nav = gallery.querySelector('[data-gallery-nav]');
      var previous = gallery.querySelector('[data-gallery-previous]');
      var next = gallery.querySelector('[data-gallery-next]');
      function updateGalleryNav() {
        if (!strip || !nav) return;
        nav.hidden = strip.scrollWidth <= strip.clientWidth + 2;
        previous.disabled = strip.scrollLeft <= 2;
        next.disabled = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;
      }
      if (strip && nav) {
        strip.addEventListener('scroll', updateGalleryNav, {passive:true});
        previous.addEventListener('click', function () {strip.scrollBy({left:-strip.clientWidth*.75,behavior:reduceMotion()?'auto':'smooth'});});
        next.addEventListener('click', function () {strip.scrollBy({left:strip.clientWidth*.75,behavior:reduceMotion()?'auto':'smooth'});});
        new ResizeObserver(updateGalleryNav).observe(strip);
        updateGalleryNav();
      }

      thumbnails.forEach(function (thumb, index) {
        thumb.addEventListener('click', function (event) {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || !mainImage) return;
          event.preventDefault();
          var request = ++imageRequest;
          var nextImage = new Image();
          gallery.setAttribute('aria-busy', 'true');
          nextImage.onload = function () {
            if (request !== imageRequest) return;
            mainImage.srcset = thumb.getAttribute('data-image-srcset');
            mainImage.src = thumb.href;
            mainImage.alt = thumb.getAttribute('data-image-alt') || '';
            if (strip) {var offset=thumb.offsetLeft-strip.offsetLeft; if(offset<strip.scrollLeft || offset+thumb.offsetWidth>strip.scrollLeft+strip.clientWidth) strip.scrollTo({left:offset-(strip.clientWidth-thumb.offsetWidth)/2,behavior:reduceMotion()?'auto':'smooth'});}
            thumbnails.forEach(function (item) {item.setAttribute('aria-current', item === thumb ? 'true' : 'false');});
            galleryStatus.textContent = mainImage.alt;
            gallery.removeAttribute('aria-busy');
            if (!reduceMotion() && mainImage.animate) mainImage.animate([{opacity:.55},{opacity:1}], {duration:200,easing:'ease-out'});
          };
          nextImage.onerror = function () {
            if (request !== imageRequest) return;
            gallery.removeAttribute('aria-busy');
            galleryStatus.textContent = root.getAttribute('data-gallery-error');
          };
          nextImage.src = thumb.href;
        });
        thumb.addEventListener('keydown', function (event) {
          var next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % thumbnails.length;
          else if (event.key === 'ArrowLeft') next = (index - 1 + thumbnails.length) % thumbnails.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = thumbnails.length - 1;
          else if (event.key === ' ') { event.preventDefault(); thumb.click(); return; }
          else return;
          event.preventDefault(); thumbnails[next].focus(); thumbnails[next].click();
        });
      });
    }


    var packs = toArray(root.querySelectorAll('[data-pack]'));
    var packList = root.querySelector('[data-pack-list]');
    var picker = root.querySelector('[data-picker]');
    if (!packs.length || !picker) return;

    var pickerHead = picker.querySelector('.rt-picker__head');
    var pickerName = picker.querySelector('[data-picker-name]');
    var pickerHint = picker.querySelector('[data-picker-hint]');
    var pickedEl = picker.querySelector('[data-picked]');
    var packSizeEl = picker.querySelector('[data-pack-size]');
    var changeBtn = picker.querySelector('[data-change-pack]');
    var seg = picker.querySelector('[data-gender-switch]');
    var segThumb = seg ? seg.querySelector('.rt-seg__thumb') : null;
    var segSettleTimer = null;
    var genderBtns = toArray(picker.querySelectorAll('[data-gender-btn]'));
    var genderLists = toArray(picker.querySelectorAll('[data-gender-list]'));
    var rangeEl = picker.querySelector('[data-gender-range]');
    var genderCounts = toArray(picker.querySelectorAll('[data-gender-count]'));
    var incBtns = toArray(picker.querySelectorAll('[data-qty-inc]'));
    var decBtns = toArray(picker.querySelectorAll('[data-qty-dec]'));
    var qtyEls = toArray(picker.querySelectorAll('[data-qty-display]'));
    var totalEl = picker.querySelector('[data-total]');
    var totalNoteEl = picker.querySelector('[data-total-note]');
    var totalSaveEl = picker.querySelector('[data-total-save]');
    var cta = picker.querySelector('[data-add-to-cart]');
    var ctaLabel = picker.querySelector('[data-cta-label]');
    var errorEl = picker.querySelector('[data-cart-error]');
    var sticky = root.querySelector('[data-sticky]');
    var stickyName = sticky ? sticky.querySelector('[data-sticky-name]') : null;
    var stickyTotal = sticky ? sticky.querySelector('[data-sticky-total]') : null;
    var stickyCta = sticky ? sticky.querySelector('[data-sticky-cta]') : null;

    var state = {
      pack: null,        // the chosen [data-pack] button
      size: 0,           // pairs in the pack
      name: '',
      totalText: '',     // Liquid-formatted pack total, copied from the row
      saveText: '',      // "Save $47.99 · Free shipping", copied from the row
      shipText: '',      // "+ $5.99 shipping" for the single pair, else ''
      quantities: {},    // variantId -> count
      gender: 'mens',
      busy: false,
      touched: false,    // has the shopper done anything yet? (gates the sticky bar)
      ctaVisible: true,
      pickerVisible: true
    };

    function sum() {
      var n = 0;
      for (var k in state.quantities) n += state.quantities[k];
      return n;
    }

    /** Pairs chosen per gender, read off the rows so the split is always
     *  what the markup says. */
    function sumByGender() {
      var out = {};
      qtyEls.forEach(function (el) {
        var row = el.closest('[data-color-row]');
        var g = row ? row.getAttribute('data-gender') : '';
        out[g] = (out[g] || 0) + (state.quantities[el.getAttribute('data-variant-id')] || 0);
      });
      return out;
    }

    function setError(message) {
      if (!errorEl) return;
      if (!message) {
        errorEl.hidden = true;
        errorEl.textContent = '';
        return;
      }
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    /* ---- Paint --------------------------------------------------------- */

    function refresh() {
      var picked = sum();
      var complete = state.size > 0 && picked === state.size;
      var atCap = picked >= state.size;

      qtyEls.forEach(function (el) {
        el.textContent = String(state.quantities[el.getAttribute('data-variant-id')] || 0);
      });
      incBtns.forEach(function (btn) {
        btn.disabled = atCap;
      });
      decBtns.forEach(function (btn) {
        btn.disabled = !(state.quantities[btn.getAttribute('data-variant-id')] > 0);
      });

      if (pickedEl) pickedEl.textContent = String(picked);
      var byGender = sumByGender();
      genderCounts.forEach(function (el) {
        var n = byGender[el.getAttribute('data-gender-count')] || 0;
        el.textContent = n > 0 ? String(n) : '';
      });
      if (packSizeEl) packSizeEl.textContent = String(state.size);
      if (totalEl) totalEl.textContent = state.totalText;
      if (totalNoteEl) totalNoteEl.textContent = state.shipText;
      if (totalSaveEl) {
        totalSaveEl.textContent = state.saveText;
        totalSaveEl.hidden = !state.saveText;
      }

      if (cta) {
        cta.disabled = !complete || state.busy;
        if (ctaLabel) {
          ctaLabel.textContent = state.busy ? 'Adding…' : complete ? 'Add to bag — ' + state.totalText : 'Add to bag';
        }
      }
      /* The sticky button is never dead. Complete, it adds; incomplete, it
       * says what is missing and sends the shopper back to the colour list.
       * A disabled dark pill at the bottom of a phone gets tapped anyway. */
      if (stickyCta) {
        stickyCta.disabled = state.busy;
        stickyCta.textContent = state.busy
          ? 'Adding…'
          : complete
            ? 'Add to bag'
            : 'Pick ' + (state.size - picked) + ' more';
      }
      if (stickyName) {
        stickyName.textContent = !state.pack ? '' : complete ? state.name : picked + ' of ' + state.size + ' chosen · ' + state.name;
      }
      if (stickyTotal) stickyTotal.textContent = state.totalText;

      updateSticky();
    }

    /* The bar carries the cart, so on a phone it arrives with the FIRST pair
     * chosen and stays until the real CTA is on screen again — the owner's
     * call: 當使用者有選擇襪子時，也要出現 add to cart 的按鈕. The picker is
     * ~960px tall on a phone, so before this the shopper could fill a whole
     * pack with no way to the cart in sight.
     *
     * Before a pair is chosen it is still only a way back: it appears once
     * the shopper has touched something AND scrolled clear of the picker
     * altogether. It never appears on arrival, because the pack preselected
     * by the section setting must not summon a bar over the hero. */
    function updateSticky() {
      if (!sticky) return;
      var chosen = sum();
      var show = Boolean(state.pack) && !state.ctaVisible &&
        (chosen > 0 || (state.touched && !state.pickerVisible));
      if (show) {
        sticky.setAttribute('data-show', '');
        sticky.setAttribute('aria-hidden', 'false');
      } else {
        sticky.removeAttribute('data-show');
        sticky.setAttribute('aria-hidden', 'true');
      }
    }

    /* ---- Step 1: packs ------------------------------------------------- */

    function selectPack(btn, opts) {
      opts = opts || {};
      var wasOpen = picker.hasAttribute('data-open');
      if (!opts.silent) state.touched = true;

      packs.forEach(function (p) {
        var on = p === btn;
        p.setAttribute('aria-checked', on ? 'true' : 'false');
        p.tabIndex = on ? 0 : -1;
      });

      state.pack = btn;
      state.size = parseInt(btn.getAttribute('data-qty'), 10) || 0;
      state.name = btn.getAttribute('data-name') || '';
      var totalNode = btn.querySelector('.rt-pack__total');
      state.totalText = totalNode ? totalNode.textContent.trim() : '';
      // The single pair's "+ $5.99 shipping" has its own node now: on the
      // phone rail .rt-pack__per is the per-pair price on every card, so it
      // can no longer double as the shipping note.
      var shipCents = parseInt(btn.getAttribute('data-ship-cents'), 10) || 0;
      var shipNode = btn.querySelector('[data-ship-note]');
      state.shipText = shipCents > 0 && shipNode ? shipNode.textContent.trim() : '';
      var saveNode = btn.querySelector('.rt-pack__save');
      state.saveText = saveNode ? saveNode.textContent.trim() : '';
      state.quantities = {};
      if (packList) packList.setAttribute('data-has-selection', '');
      setError('');

      // The head names the pack, because the chosen row scrolls out of view
      // as the picker arrives: "The Workweek / 6 pairs · 0 of 6 chosen".
      if (pickerName) pickerName.textContent = state.name;
      if (pickerHint) pickerHint.textContent = state.size + (state.size === 1 ? ' pair' : ' pairs');

      picker.setAttribute('data-open', '');
      picker.setAttribute('aria-hidden', 'false');
      refresh();
      revealPackCard(btn, !opts.silent);

      // Bring the picker into view only when it is actually below the fold.
      // HIG: motion follows the gesture; a tap on the row, the sheet arrives
      // beneath it. Reduce Motion gets a jump instead of a smooth scroll.
      if (!wasOpen && pickerHead && !opts.silent) {
        window.requestAnimationFrame(function () {
          var rect = pickerHead.getBoundingClientRect();
          if (rect.top > window.innerHeight * 0.55) {
            pickerHead.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
          }
        });
      }
    }

    /* On a phone the four packs are a horizontal rail, so the chosen card
     * has to be brought to the CENTRE of it — including the silent preselect,
     * where the shopper would otherwise arrive looking at "1 pair" while the
     * picker underneath says The Workweek. Centre, not the left edge: the
     * cards snap to centre so that a slice of the pack on either side stays
     * visible, which is the whole affordance (owner: 讓使用者清楚感知到是可以
     * 左右滑的). Only the container scrolls, never the page. On desktop the
     * list is a grid that does not overflow, so the width test makes this a
     * no-op there. */
    function revealPackCard(btn, smooth) {
      if (!packList) return;
      if (packList.scrollWidth <= packList.clientWidth + 4) return;
      var left = packList.scrollLeft + btn.getBoundingClientRect().left -
        packList.getBoundingClientRect().left -
        (packList.clientWidth - btn.getBoundingClientRect().width) / 2;
      left = Math.max(0, Math.min(Math.round(left), packList.scrollWidth - packList.clientWidth));
      if (Math.abs(left - packList.scrollLeft) < 2) return;
      if (packList.scrollTo) {
        packList.scrollTo({ left: left, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
      } else {
        packList.scrollLeft = left;
      }
    }

    function closePicker() {
      packs.forEach(function (p, i) {
        p.setAttribute('aria-checked', 'false');
        p.tabIndex = i === 0 ? 0 : -1;
      });
      state.pack = null;
      state.size = 0;
      state.name = '';
      state.totalText = '';
      state.shipText = '';
      state.saveText = '';
      state.quantities = {};
      if (packList) packList.removeAttribute('data-has-selection');
      hideLens();
      picker.removeAttribute('data-open');
      picker.setAttribute('aria-hidden', 'true');
      setError('');
      refresh();
    }

    packs.forEach(function (btn, i) {
      btn.tabIndex = i === 0 ? 0 : -1;
      btn.addEventListener('click', function () {
        selectPack(btn);
      });
      // Radio-group keyboard model: arrows move AND select.
      btn.addEventListener('keydown', function (event) {
        var delta = 0;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') delta = 1;
        else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') delta = -1;
        else return;
        event.preventDefault();
        var next = packs[(i + delta + packs.length) % packs.length];
        next.focus();
        selectPack(next);
      });
    });

    if (changeBtn) {
      changeBtn.addEventListener('click', function () {
        closePicker();
        var list = root.querySelector('[data-pack-list]');
        if (list) list.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        if (packs[0]) packs[0].focus({ preventScroll: true });
      });
    }

    /* ---- Step 2a: gender ----------------------------------------------- */

    function setGender(g) {
      var previousGender = state.gender;
      var changed = g !== state.gender;
      state.gender = g;
      hideLens();
      if (seg) {
        if (changed && segThumb && !reduceMotion()) {
          // Liquid Glass, in three beats like the reference: LIFT (120ms,
          // scale + glass), TRAVEL (the slide starts 90ms in, on the thumb
          // spring), SETTLE (the lift comes off once the slide has landed).
          // State is applied immediately for everything that is not the
          // thumb, so the lists and labels never wait on the animation.
          segThumb.classList.add('is-lifted');
          void segThumb.offsetWidth;
          if (segSettleTimer) window.clearTimeout(segSettleTimer);
          window.setTimeout(function () {
            if (state.gender === g) seg.setAttribute('data-active', g);
          }, 90);
          segSettleTimer = window.setTimeout(function () {
            segThumb.classList.remove('is-lifted');
            segSettleTimer = null;
          }, 90 + 300);
        } else {
          seg.setAttribute('data-active', g);
        }
      }
      genderBtns.forEach(function (b) {
        var on = b.getAttribute('data-gender-btn') === g;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      var direction = g === 'womens' ? 1 : -1;
      var leavingList = genderLists.find(function(list){return list.getAttribute('data-gender-list')===previousGender;});
      if(leavingList) leavingList.parentElement.style.position='relative';
      var leavingTop = leavingList ? leavingList.offsetTop : 0;
      genderLists.forEach(function(list){
        list.getAnimations().forEach(function(a){a.cancel();});
        list.style.position='';list.style.top='';list.style.left='';list.style.width='';
        var active=list.getAttribute('data-gender-list')===g;
        var outgoing=list.getAttribute('data-gender-list')===previousGender;
        list.inert=!active;list.setAttribute('aria-hidden',active?'false':'true');
        if(changed && !reduceMotion() && outgoing && !active){
          list.parentElement.style.position='relative';
          var top=leavingTop;
          list.hidden=false;list.style.position='absolute';list.style.top=top+'px';list.style.left='0';list.style.width='100%';
          var exit=list.animate([{opacity:1,transform:'translateX(0)'},{opacity:0,transform:'translateX('+(-direction*28)+'px)'}],{duration:320,easing:'cubic-bezier(.22,1,.36,1)'});
          exit.onfinish=function(){if(state.gender!==list.getAttribute('data-gender-list')){list.hidden=true;list.style.position='';list.style.top='';list.style.left='';list.style.width='';}};
        }else{
          list.hidden=!active;
          if(changed && active && !reduceMotion())list.animate([{opacity:0,transform:'translateX('+(direction*28)+'px)'},{opacity:1,transform:'translateX(0)'}],{duration:320,easing:'cubic-bezier(.22,1,.36,1)'});
        }
      });
      if (rangeEl) {
        rangeEl.textContent = rangeEl.getAttribute('data-range-' + g) || '';
        rangeEl.getAnimations().forEach(function(a){a.cancel();});
        if(changed && !reduceMotion()) rangeEl.animate([{opacity:.25},{opacity:1}],{duration:220,easing:'ease-out'});
      }
    }

    genderBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        setGender(b.getAttribute('data-gender-btn'));
      });
      b.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        var idx = genderBtns.indexOf(b);
        var next = genderBtns[(idx + (event.key === 'ArrowRight' ? 1 : -1) + genderBtns.length) % genderBtns.length];
        next.focus();
        setGender(next.getAttribute('data-gender-btn'));
      });
    });
    if (seg && seg.getAttribute('data-active')) {
      setGender(seg.getAttribute('data-active'));
    } else {
      setGender('mens');
    }

    /* ---- The colour lens: hover on a fine pointer, tap on touch ---------- */

    var lens = root.querySelector('[data-lens]');
    var lensImg = lens ? lens.querySelector('[data-lens-img]') : null;
    var lensName = lens ? lens.querySelector('[data-lens-name]') : null;
    var lensFor = null;          // the [data-peek] button the lens belongs to
    var lensHideTimer = null;
    var finePointer = Boolean(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

    function placeLens(btn) {
      var r = btn.getBoundingClientRect();
      var w = lens.offsetWidth || 172;
      var h = lens.offsetHeight || 240;
      var gap = 10;
      var vw = window.innerWidth;
      var vh = window.innerHeight;
      // Prefer above the swatch, left edges aligned; flip below when there is
      // no room; clamp inside the viewport with a 12px inset.
      var left = Math.min(Math.max(12, r.left - 8), vw - w - 12);
      var above = r.top - gap - h >= 12;
      var top = above ? r.top - gap - h : Math.min(r.bottom + gap, vh - h - 12);
      lens.style.left = left + 'px';
      lens.style.top = top + 'px';
      var ox = Math.max(0, Math.min(w, r.left + r.width / 2 - left));
      lens.style.setProperty('--rt-lens-origin', ox + 'px ' + (above ? '100%' : '0%'));
    }

    function showLens(btn) {
      if (!lens || !lensImg) return;
      if (lensHideTimer) { window.clearTimeout(lensHideTimer); lensHideTimer = null; }
      if (lensFor && lensFor !== btn) lensFor.setAttribute('aria-expanded', 'false');
      lensFor = btn;
      var src = btn.getAttribute('data-peek-src') || '';
      if (lensImg.getAttribute('src') !== src) lensImg.setAttribute('src', src);
      lensImg.alt = btn.getAttribute('data-peek-name') || '';
      if (lensName) lensName.textContent = btn.getAttribute('data-peek-name') || '';
      lens.hidden = false;
      lens.setAttribute('aria-hidden', 'false');
      placeLens(btn);
      btn.setAttribute('aria-expanded', 'true');
      // A forced reflow, not requestAnimationFrame: the unhide and the
      // data-show must land in different style passes for the transition to
      // run, and rAF is throttled or frozen in background and automation tabs.
      void lens.offsetWidth;
      lens.setAttribute('data-show', '');
    }

    function hideLens() {
      if (!lens || !lensFor) return;
      var btn = lensFor;
      lensFor = null;
      btn.setAttribute('aria-expanded', 'false');
      lens.removeAttribute('data-show');
      lens.setAttribute('aria-hidden', 'true');
      if (lensHideTimer) window.clearTimeout(lensHideTimer);
      lensHideTimer = window.setTimeout(function () {
        lensHideTimer = null;
        if (!lensFor) lens.hidden = true;
      }, 200);
    }

    if (lens) {
      toArray(picker.querySelectorAll('[data-peek]')).forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('pointerenter', function (e) {
          if (e.pointerType === 'mouse' && finePointer) showLens(btn);
        });
        btn.addEventListener('pointerleave', function (e) {
          if (e.pointerType === 'mouse' && finePointer) hideLens();
        });
        btn.addEventListener('click', function () {
          // Touch and keyboard: a tap toggles; a second tap (or a tap on
          // another swatch, or anywhere else) closes it.
          if (lensFor === btn) hideLens();
          else showLens(btn);
        });
      });
      document.addEventListener('pointerdown', function (e) {
        if (lensFor && !lensFor.contains(e.target)) hideLens();
      }, true);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') hideLens();
      });
      var scroller = document.querySelector('.page-wrapper') || window;
      scroller.addEventListener('scroll', function () { if (lensFor) hideLens(); }, { passive: true });
      window.addEventListener('resize', function () { if (lensFor) placeLens(lensFor); });
    }

    /* ---- Step 2b: steppers --------------------------------------------- */

    incBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (sum() >= state.size) return;
        state.touched = true;
        var id = btn.getAttribute('data-variant-id');
        state.quantities[id] = (state.quantities[id] || 0) + 1;
        setError('');
        refresh();
      });
    });
    decBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-variant-id');
        if (!(state.quantities[id] > 0)) return;
        state.quantities[id] -= 1;
        if (state.quantities[id] === 0) delete state.quantities[id];
        setError('');
        refresh();
      });
    });

    /* ---- Step 3: add to cart (additive) -------------------------------- */

    function addToCart() {
      if (state.busy || !state.pack || sum() !== state.size) return;
      if (root.getAttribute('data-checkout-ready') !== 'true') { setError(root.getAttribute('data-preview-error')); return; }
      state.busy = true;
      setError('');
      refresh();

      var items = [];
      for (var id in state.quantities) {
        if (state.quantities[id] > 0) items.push({ id: parseInt(id, 10), quantity: state.quantities[id] });
      }

      var deferred = makeDeferred();
      var announce = new Event(CART_LINES_UPDATE, { bubbles: true, composed: true });
      announce.action = 'add';
      announce.context = 'product';
      announce.source = SOURCE;
      announce.promise = deferred.promise;
      document.dispatchEvent(announce);

      var payload = { items: items };
      var sectionIds = cartSectionIds();
      if (sectionIds.length) payload.sections = sectionIds.join(',');

      fetch(shopRoot() + '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) {
              var e = new Error((data && data.description) || 'Could not add to your cart');
              e.userFacing = true;
              throw e;
            }
            return data;
          });
        })
        .then(function (data) {
          // /cart/add.js answers with the added lines (plus `sections`), not
          // the whole cart. Horizon's component wants the cart shape, so
          // fetch it once; the sections HTML from the add is what it paints.
          return fetch(shopRoot() + '/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              cart.sections = data.sections;
              return cart;
            });
        })
        .then(function (cart) {
          state.busy = false;
          // didError:false is required or Horizon's cart component will not
          // re-render and the header count goes stale.
          deferred.resolve({
            cart: cart,
            detail: {
              items: cart.items,
              sections: cart.sections,
              itemCount: cart.item_count,
              source: SOURCE,
              sourceId: SOURCE,
              didError: false
            }
          });
          closePicker();
          // One tick, so the cart component can consume the event and swap
          // in the re-rendered sections before the drawer is shown.
          window.setTimeout(function () {
            if (!openCartDrawer()) window.location.href = shopRoot() + '/cart';
          }, 0);
        })
        .catch(function (err) {
          state.busy = false;
          deferred.resolve({ cart: null, detail: { didError: true, source: SOURCE } });
          refresh();
          setError(err && err.userFacing ? err.message : 'Could not add to your cart — please try again.');
        });
    }

    if (cta) cta.addEventListener('click', addToCart);
    if (stickyCta) {
      stickyCta.addEventListener('click', function () {
        if (state.size > 0 && sum() === state.size) {
          addToCart();
          return;
        }
        // Incomplete: take them to the colour list rather than doing nothing.
        if (pickerHead) {
          pickerHead.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        }
      });
    }

    /* ---- Sticky bar: only while the real CTA is off-screen ------------- */

    if (sticky && cta && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].target === cta) state.ctaVisible = entries[i].isIntersecting;
          else if (entries[i].target === picker) state.pickerVisible = entries[i].isIntersecting;
        }
        updateSticky();
      }, { threshold: 0 });
      io.observe(cta);
      io.observe(picker);
    } else {
      state.ctaVisible = true;
    }

    refresh();

    // The pack chosen on arrival (section setting). Silent: no scroll, no
    // sticky bar, no "touched" — the picker simply starts open, at zero.
    var defaultQty = root.getAttribute('data-default-pack');
    if (defaultQty && defaultQty !== '0') {
      var preset = packs.filter(function (p) { return p.getAttribute('data-qty') === defaultQty; })[0];
      if (preset) {
        // Open instantly, not on the sheet curve: nothing was tapped.
        var body = picker.querySelector('.rt-picker__body');
        picker.style.transition = 'none';
        if (body) body.style.transition = 'none';
        selectPack(preset, { silent: true });
        requestAnimationFrame(function(){revealPackCard(preset,false);});
        var lastPackWidth=0;
        new ResizeObserver(function(){
          if(packList.clientWidth===lastPackWidth)return;
          lastPackWidth=packList.clientWidth;
          requestAnimationFrame(function(){var active=packs.find(function(p){return p.getAttribute('aria-checked')==='true';});if(active)revealPackCard(active,false);});
        }).observe(packList);
        if(document.fonts)document.fonts.ready.then(function(){var active=packs.find(function(p){return p.getAttribute('aria-checked')==='true';});if(active)revealPackCard(active,false);});

        void picker.offsetHeight;
        picker.style.transition = '';
        if (body) body.style.transition = '';
      }
    }
  }

  function initAll(scope) {
    toArray((scope || document).querySelectorAll('[data-rt-packs]')).forEach(initPacks);
  }

  initAll(document);
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();



